import { makeStyles } from "@mui/styles";
import type { Theme } from "src/theme";

const SCENARIO_CONTAINER_HEIGHT = 300;
const SCENARIO_CARD_MARGIN_BOTTOM = 40;
const SCENARIO_CARD_HEIGHT = SCENARIO_CONTAINER_HEIGHT - SCENARIO_CARD_MARGIN_BOTTOM;

export const useScenarioCardStyles = makeStyles((theme: Theme) => ({
    root: {
        "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "1265px",
        },
    },
    marginTop: {
        marginTop: 12,
    },
    marginBottom: {
        marginBottom: 32,
    },
    marginBottomSmall: {
        marginBottom: 12,
    },
    subTitle: {
        fontStyle: "normal",
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "24px",
    },
    templateScenarioText: {
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "normal",
    },
    ScenarioContainer: {
        height: `${SCENARIO_CONTAINER_HEIGHT}px`,
    },
    ScenarioCard: {
        position: "relative",
        height: `${SCENARIO_CARD_HEIGHT}px`,
        minWidth: "170px",
        background: "#F2F1F9",
        borderRadius: "4px 4px 0px 0px",
        padding: "16px",
        marginBottom: `${SCENARIO_CARD_MARGIN_BOTTOM}px`,
    },
    ScenarioCardHover: {
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
            background: "#DEDBF0",
        },
    },
    ScenarioError: {
        color: "#F44336",
    },
    ScenarioCardDisabled: {
        background: "#F2F3F6",
        "&:hover": {
            background: "#F2F3F6",
        },
        cursor: "not-allowed",
    },
    ScenarioCardTitleContainer: {
        marginBottom: 8,
    },
    ScenarioCardIntervalContainer: {
        width: "100%",
        backgroundColor: "white",
        padding: 8,
        borderRadius: 4,
        marginBottom: 8,
    },
    gridContainer: {
        gap: 8,
        overflowX: "auto",
        zIndex: 2,
        position: "relative",
        display: "flex",
        height: `${SCENARIO_CONTAINER_HEIGHT}px`,
        overflowY: "hidden",
    },
    gridFadeEffectLeft: {
        zIndex: 999,
        position: "absolute",
        height: `${SCENARIO_CARD_HEIGHT}px`,
        minWidth: "50px",
        background: `
        linear-gradient(90deg, rgba(255,255,255,1) 0%, 
        rgba(255,255,255,0) 100%, 
        rgba(255,255,255,0) 100%)`,
        left: 0,
    },
    gridFadeEffectRight: {
        zIndex: 999,
        position: "absolute",
        height: `${SCENARIO_CARD_HEIGHT}px`,
        minWidth: "50px",
        background: `
        linear-gradient(90deg, rgba(255,255,255,1) 0%,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,1) 100%)`,
        right: "-1px",
    },
    boldAndSmallText: {
        fontWeight: "bold",
        fontSize: "12px",
    },
    ScenarioDisplayLineContainer: {
        display: "flex",
        alignItems: "center",
    },
    ScenarioDisplayLineIconContainer: {
        marginRight: 8,
    },
    ScenarioDisplayLineIcon: {
        color: "#394FFD",
        height: 16,
        width: 16,
    },
    ScenarioMissingOfferDisplayLineIcon: {
        height: 20,
        width: 20,
    },
    ScenarioPrimaryColor: {
        color: "#394FFD",
    },
    ScenarioDisabledColor: {
        color: "#999CA2",
    },
    ScenarioDisplayLineTextContainer: {
        display: "flex",
        flexDirection: "column",
    },
    ScenarioDisplayLineText: {
        fontSize: 10,
        textTransform: "uppercase",
    },
    SpacerDisplayButton: {
        color: "#394DFF",
        cursor: "pointer",
    },
    SpacerDisplayButtonDisabled: {
        color: "#bdbdbd",
        cursor: "not-allowed",
    },
    ScenarioDisplayTitle: {
        fontSize: "var(--ds-fontSize--XS)",
        fontWeight: 500,
        color: "var(--ds-color--surface-300)",
    },
    ScenarioDisplayTitleWarningBordered: {
        border: "1px solid var(--ds-color--error-500) !important",
    },
    ScenarioDisplayTitleWarning: {
        fontSize: "var(--ds-fontSize--XS)",
        fontWeight: 500,
        color: "var(--ds-color--error-500)!important",
    },
    ScenarioDisplayIcon: {
        height: 14,
        width: 14,
    },
    ScenarioDisplayLineValueContainer: {
        marginBottom: 8,
    },
    ScenarioTitleActionAlign: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    ScenarioDisplayCrossContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 24,
        width: 24,
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        borderRadius: "4px",
        "&:hover": {
            background: "#CAC6E2",
        },
    },
    ScenarioDisplayCross: {
        height: 16,
        width: 16,
    },
    ScenarioGridTitle: {
        fontSize: 14,
        fontWeight: "bold",
    },
    ScenarioGridButtonContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "200px",
    },
    ScenarioProductList: {
        cursor: "pointer",
    },

    // DIALOG
    scenarioDialogContainer: {
        width: 750,
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 64,
        paddingRight: 64,
    },
    titleScenarioDialog: {
        fontSize: 24,
        fontWeight: 500,
    },
    subTitleScenarioDialogContainer: {
        marginTop: 24,
        marginBottom: 24,
    },
    subTitleScenarioDialog: {
        fontSize: 16,
        fontWeight: "bold",
    },
    edgesScenarioContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 24,
    },
    edgesScenarioLeft: {
        minWidth: "70px",
    },
    edgesScenarioRight: {
        minWidth: "70px",
    },
    alertScenarioDialogContainer: {
        marginBottom: 24,
    },
    scenarioDialogDefaultContainer: {
        marginBottom: 24,
    },

    // STRIPES CONTAINER
    spacerContainer: {
        minWidth: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 40,
    },
    stripesBackground: {
        background:
            "linear-gradient(45deg, #F2F1F9 12.5%, #fff 12.5%, #fff 37.5%, #F2F1F9 37.5%, #F2F1F9 62.5%, #fff 62.5%, #fff 87.5%, #F2F1F9 87.5%)",
        backgroundSize: "35px 35px",
        backgroundPosition: "50px 50px",
    },
    // LEGENDS
    legendContainer: {
        alignItems: "center",
        width: "100%",
        justifyContent: "center",
        gap: 28,
        display: "flex",
        marginBottom: 24,
    },
    leftLegendBox: {
        width: 17,
        height: 17,
        background: "#F2F1F9",
        marginRight: 8,
    },
    rightLegendBox: {
        width: 17,
        height: 17,
        background:
            "linear-gradient(45deg, #F2F1F9 12.5%, #fff 12.5%, #fff 37.5%, #F2F1F9 37.5%, #F2F1F9 62.5%, #fff 62.5%, #fff 87.5%, #F2F1F9 87.5%)",
        backgroundSize: "8px 8px",
        backgroundPosition: "50px 50px",
        marginRight: 8,
    },
    // ARROW UNDER GRID
    arrowContainer: {
        position: "absolute",
        width: "100%",
        bottom: 0,
        left: 0,
    },
    arrowLeftUnderGrid: {
        position: "absolute",
        left: 0,
        top: "-73px",
        display: "block",
        margin: "30px auto",
        width: "8px",
        height: "8px",
        borderTop: "2px solid #9E9E9E",
        borderLeft: "2px solid #9E9E9E",
        transform: "rotate(-45deg)",
        zIndex: 999,
    },
    arrowRightUnderGrid: {
        position: "absolute",
        right: 0,
        top: "-73px",
        display: "block",
        margin: "30px auto",
        width: "8px",
        height: "8px",
        borderTop: "2px solid #9E9E9E",
        borderLeft: "2px solid #9E9E9E",
        transform: "rotate(135deg)",
        zIndex: 999,
    },
    arrowMiddleUnderGrid: {
        borderTop: "2px solid #9E9E9E",
        width: "100%",
        zIndex: 999,
        position: "absolute",
        top: "-40px",
    },
    arrowLegendLess: {
        position: "absolute",
        top: -31,
        color: "#9E9E9E",
    },
    arrowLegendMore: {
        position: "absolute",
        top: -31,
        color: "#9E9E9E",
        right: 0,
    },
    // ALERT
    textColor: {
        color: theme.palette.primary.main,
    },
    textBold: {
        fontWeight: 600,
    },
    titleScenarioAlert: {
        fontSize: 16,
        fontWeight: 600,
    },
    alertBackground: {
        backgroundColor: `${theme.palette.alertBackground.main} !important`,
    },
    alertIconColor: {
        color: `${theme.palette.primary.main} !important`,
    },
    popper: {
        minWidth: "150px !important",
    },
    displayError: {
        display: "block",
    },
    notDisplayError: {
        display: "none",
    },
    // REFERENCE TAB
    referenceValueCell: {
        minWidth: theme.spacing(12),
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
    },
    referenceDialogTitle: {
        whiteSpace: "nowrap",
        "&:first-letter": {
            "text-transform": "capitalize",
        },
    },
    specificCaseContainerContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    specificCaseContainer: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        borderRadius: "4px",
        border: "1px solid var(--ds-color--neutral-100)",
    },
    specificCaseContainerClosed: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    specificCaseContainerOpened: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    specificCaseLabelContainer: {
        justifyContent: "space-between",
        width: "100%",
        marginLeft: 0,
        marginRight: 0,
    },
    specificCaseTitle: {
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "22px",
    },
    scenarioTitle: {
        display: "flex",
        padding: "var(--ds-spacing--XS) var(--ds-spacing--S)",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "var(--ds-spacing--S)",
        borderRadius: "24px",
    },
    posFieldContainer: {
        marginBottom: 8,
    },
    duplicateSubTitleText: {
        fontWeight: 400,
    },
    cardContentContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 24,
    },
    subCardContentContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 16,
    },
}));
